// src/pages/documents/PdfVisualMapper.tsx

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { 
  Typography, 
  Button, 
  Spin, 
  Alert, 
  Breadcrumb,
  Space,
  Select,
  Card,
  Tag,
  Drawer,
  message,
  Input,
  Empty,
  Tooltip,
  Radio,
  Switch
} from 'antd';
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  LeftOutlined,
  RightOutlined,
  SaveOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SearchOutlined,
  ReloadOutlined,
  SettingOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { PDFDocument, PDFTextField, PDFCheckBox, PDFRadioGroup, PDFDropdown } from 'pdf-lib';
import { PdfFieldMapping, MAPPABLE_ENTITIES, mappableEntityOptions } from '../../types';
import EntityRowSelector from './EntityRowSelector';
import { probateDocsService } from './probatedocs.service';
import api from '../../services/api';

// Set up PDF.js worker
// Use a specific version if pdfjs.version is not reliable or if unpkg is flaky
// For react-pdf 9.x, it uses pdfjs-dist 4.4.168 usually.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const { Title, Text } = Typography;
const { Option } = Select;

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 150px);
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
  flex-shrink: 0;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const PdfViewerContainer = styled.div`
  flex: 1;
  position: relative;
  background: #374151;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PdfControlBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: #1F2937;
  color: #ffffff;
  flex-shrink: 0;
`;

const PdfWrapper = styled.div<{ $scale: number }>`
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;
  padding: 20px;
  background: #525659;
  
  .react-pdf__Document {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .react-pdf__Page {
    margin-bottom: 20px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    position: relative;
  }
`;

const FieldOverlay = styled.div<{ $isSelected: boolean; $isMapped: boolean }>`
  position: absolute;
  cursor: pointer;
  background-color: ${props => 
    props.$isSelected ? 'rgba(59, 130, 246, 0.4)' : 
    props.$isMapped ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'};
  border: 2px solid ${props => 
    props.$isSelected ? '#3B82F6' : 
    props.$isMapped ? '#10B981' : '#F59E0B'};
  z-index: 10;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => 
      props.$isSelected ? 'rgba(59, 130, 246, 0.5)' : 
      props.$isMapped ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'};
    z-index: 20;
  }
`;

const PdfLoadingContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const FieldsSidebar = styled.div`
  width: 400px;
  background: #ffffff;
  border-left: 1px solid #E5E7EB;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #E5E7EB;
  background: #F9FAFB;
`;

const LegendContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #E5E7EB;
  font-size: 11px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4B5563;
  
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
`;

const SelectedFieldBanner = styled.div`
  padding: 12px 16px;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  border-bottom: 1px solid #1D4ED8;
  color: #ffffff;
  
  .selected-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.8;
    margin-bottom: 4px;
  }
  
  .selected-name {
    font-size: 14px;
    font-weight: 600;
    font-family: monospace;
  }
  
  .selected-context {
    font-size: 12px;
    opacity: 0.9;
    font-style: italic;
    margin-top: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
`;

const FieldCard = styled(Card)<{ $isMapped: boolean; $isSelected: boolean }>`
  margin-bottom: 8px;
  cursor: pointer;
  border: 2px solid ${props => 
    props.$isSelected ? '#3B82F6' : 
    props.$isMapped ? '#10B981' : '#F59E0B'};
  background: ${props => 
    props.$isSelected ? '#EFF6FF' :
    props.$isMapped ? '#ECFDF5' : '#FFFBEB'};
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  .ant-card-body {
    padding: 12px;
  }
`;

const FieldName = styled.div`
  font-family: monospace;
  font-size: 13px;
  font-weight: 600;
  color: #1F2937;
  margin-bottom: 4px;
`;

const FieldContext = styled.div`
  font-size: 12px;
  color: #6B7280;
  font-style: italic;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FieldMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #9CA3AF;
`;

const MappingInfo = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: #F3F4F6;
  border-radius: 4px;
  font-size: 12px;
  color: #374151;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  .ant-breadcrumb-link {
    cursor: pointer;
  }
`;

const SearchInput = styled(Input)`
  margin-bottom: 12px;
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  label {
    font-weight: 500;
    color: #374151;
  }
`;

const ContextBox = styled.div`
  background: #F3F4F6;
  padding: 12px;
  border-radius: 6px;
  font-style: italic;
  color: #4B5563;
  border-left: 3px solid #6366F1;
`;

const PositionInfo = styled.div`
  background: #FEF3C7;
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
  
  h5 {
    margin: 0 0 8px 0;
    color: #92400E;
  }
  
  p {
    margin: 0;
    color: #A16207;
  }
`;

const PageIndicator = styled.span`
  background: #E5E7EB;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
`;

interface PdfVisualMapperProps {
  probateDocId?: string;
}

interface ConcatPart {
  entity: string;
  field: string;
  /** Which row, for entities an estate holds several of. "" is the first. */
  selector?: string;
}

const buildTemplate = (parts: ConcatPart[], separator: string): string =>
  parts
    .filter(p => p.entity && p.field)
    .map(p => `{{${p.entity}${p.selector ?? ''}.${p.field}}}`)
    .join(separator);

/**
 * Parses a template like "{{user.firstName}} {{user.lastName}}" back into
 * entity/field parts + a uniform separator. Returns null when the template
 * uses expressions the simple builder can't represent (mixed separators,
 * unknown entities or fields), and the raw editor is used instead.
 */
const parseTemplate = (template: string): { parts: ConcatPart[]; separator: string } | null => {
  const regex = /\{\{\s*([^}]+?)\s*\}\}/g;
  const parts: ConcatPart[] = [];
  const literals: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    literals.push(template.slice(lastIndex, match.index));
    // A selector sits between the entity and the field: "asset[1].marketValue".
    const expr = match[1].match(/^(\w+)(\[[^\]]*\])?\.(.+)$/);
    if (!expr) return null;
    const [, entity, selector, fieldKey] = expr;
    const known = MAPPABLE_ENTITIES[entity]?.fields.some(f => f.value === fieldKey);
    if (!known) return null;
    parts.push({ entity, field: fieldKey, selector: selector ?? '' });
    lastIndex = regex.lastIndex;
  }
  literals.push(template.slice(lastIndex));

  if (parts.length === 0) return null;
  if (literals[0] !== '' || literals[literals.length - 1] !== '') return null;
  const separators = literals.slice(1, -1);
  const separator = separators.length > 0 ? separators[0] : ' ';
  if (separators.some(s => s !== separator)) return null;

  return { parts, separator };
};

const PdfVisualMapper: React.FC<PdfVisualMapperProps> = (props) => {
  const { probateDocId: routeDocId } = useParams<{ probateDocId: string }>();
  const probateDocId = props.probateDocId || routeDocId || '';
  const navigate = useNavigate();
  
  // State
  const [fields, setFields] = useState<PdfFieldMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedField, setSelectedField] = useState<PdfFieldMapping | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [scale, setScale] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageDimensions, setPageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [invertY, setInvertY] = useState(true); // Default to true (standard PDF bottom-left)
  
  // State for Data URL
  const [fileDataUrl, setFileDataUrl] = useState<string | null>(null);

  // Create file object from pdfData to avoid network stack errors
  const fileObj = useMemo(() => {
    if (!pdfData) return null;
    return { data: pdfData };
  }, [pdfData]);
  
  // Mapping state for selected field
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [selectedFieldKey, setSelectedFieldKey] = useState<string>('');
  // Which row, for entities an estate holds several of. "" is the first row.
  const [entitySelector, setEntitySelector] = useState<string>('');

  // Concatenation (template) mapping state
  const [mappingMode, setMappingMode] = useState<'single' | 'concat'>('single');
  const [concatParts, setConcatParts] = useState<ConcatPart[]>([{ entity: '', field: '' }]);
  const [separator, setSeparator] = useState<string>(' ');
  const [advancedMode, setAdvancedMode] = useState(false);
  const [rawTemplate, setRawTemplate] = useState<string>('');

  // Visual test-fill state: pdfFieldName -> test value shown in the preview
  const [testFills, setTestFills] = useState<Record<string, string>>({});
  const [testValue, setTestValue] = useState<string>('');
  const [displayPdfData, setDisplayPdfData] = useState<ArrayBuffer | Uint8Array | null>(null);
  
  // Load PDF as ArrayBuffer
  const loadPdf = useCallback(async () => {
    if (!probateDocId) return;
    
    setPdfLoading(true);
    setPdfError(null);
    
    try {
      const response = await api.get(`/probatedocs/${probateDocId}/download/original`, {
        responseType: 'arraybuffer',
      });
      
      setPdfData(response.data);
    } catch (err: any) {
      console.error('Failed to load PDF:', err);
      setPdfError(err.response?.data?.message || 'Failed to load PDF document');
    } finally {
      setPdfLoading(false);
    }
  }, [probateDocId]);

  // Fetch field mappings
  const fetchMappings = useCallback(async () => {
    if (!probateDocId) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await probateDocsService.getFieldMappings(probateDocId);
      setFields(data);
    } catch (err: any) {
      console.error('Failed to fetch mappings:', err);
      if (err.response?.status !== 404) {
        setError(err.response?.data?.message || 'Failed to fetch field mappings');
      }
    } finally {
      setLoading(false);
    }
  }, [probateDocId]);

  useEffect(() => {
    fetchMappings();
    loadPdf();
    // No cleanup needed for ArrayBuffer
  }, [fetchMappings, loadPdf]);

  // Apply test fills (client-side, preview only) on top of the original PDF
  useEffect(() => {
    let cancelled = false;

    const apply = async () => {
      if (!pdfData) {
        setDisplayPdfData(null);
        return;
      }
      if (Object.keys(testFills).length === 0) {
        setDisplayPdfData(pdfData);
        return;
      }
      try {
        const doc = await PDFDocument.load(pdfData.slice(0), { ignoreEncryption: true });
        const form = doc.getForm();
        for (const [name, value] of Object.entries(testFills)) {
          try {
            const formField = form.getField(name);
            if (formField instanceof PDFTextField) {
              const maxLen = formField.getMaxLength();
              formField.setText(maxLen ? value.slice(0, maxLen) : value);
            } else if (formField instanceof PDFCheckBox) {
              formField.check();
            } else if (formField instanceof PDFRadioGroup || formField instanceof PDFDropdown) {
              const options = formField.getOptions();
              if (options.length > 0) formField.select(options[0]);
            }
          } catch (e) {
            console.warn(`Test fill failed for field "${name}":`, e);
          }
        }
        const bytes = await doc.save();
        if (!cancelled) setDisplayPdfData(bytes);
      } catch (e) {
        console.error('Failed to apply test fills:', e);
        message.error('Failed to apply test fill to the PDF preview');
        if (!cancelled) setDisplayPdfData(pdfData);
      }
    };

    apply();
    return () => { cancelled = true; };
  }, [pdfData, testFills]);

  useEffect(() => {
    if (!displayPdfData) {
      setFileDataUrl(null);
      return;
    }

    const blob = new Blob([displayPdfData], { type: 'application/pdf' });
    const reader = new FileReader();
    reader.onload = () => {
      setFileDataUrl(reader.result as string);
    };
    reader.onerror = () => {
      console.error('Failed to convert PDF to Data URL');
      setPdfError('Failed to process PDF data');
    };
    reader.readAsDataURL(blob);
  }, [displayPdfData]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  }, []);

  const onPageLoadSuccess = useCallback((page: any) => {
    // page.originalWidth and page.originalHeight are in PDF points (usually 72 DPI)
    setPageDimensions({
      width: page.originalWidth,
      height: page.originalHeight,
    });
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF Load Error:', error);
    setPdfError(`Failed to load PDF: ${error.message}`);
    setPdfLoading(false);
  }, []);

  // Handle field selection
  const handleFieldSelect = (field: PdfFieldMapping) => {
    console.log('Selected field:', field);
    if (!field.uid && !field.id) {
      console.error('Selected field is missing UID and ID:', field);
      message.error('Error: Selected field has no ID');
    }
    setSelectedField(field);
    setTestValue(testFills[field.pdfFieldName] ?? `>>> TEST: ${field.pdfFieldName} <<<`);

    // Parse existing mapping
    if (field.template) {
      // Template supersedes the single mapping — open in concat mode
      const parsed = parseTemplate(field.template);
      setMappingMode('concat');
      if (parsed) {
        setConcatParts(parsed.parts);
        setSeparator(parsed.separator);
        setAdvancedMode(false);
      } else {
        // Too complex for the builder — fall back to raw editing
        setConcatParts([{ entity: '', field: '' }]);
        setSeparator(' ');
        setAdvancedMode(true);
      }
      setRawTemplate(field.template);
      setSelectedEntity('');
      setSelectedFieldKey('');
      setEntitySelector('');
    } else {
      setMappingMode('single');
      setConcatParts([{ entity: '', field: '' }]);
      setSeparator(' ');
      setAdvancedMode(false);
      setRawTemplate('');
      if (field.relatedEntity && field.databaseFieldName) {
        const parts = field.databaseFieldName.split('.');
        setSelectedEntity(field.relatedEntity);
        setSelectedFieldKey(parts.length > 1 ? parts.slice(1).join('.') : '');
        setEntitySelector(field.entitySelector || '');
      } else {
        setSelectedEntity('');
        setSelectedFieldKey('');
        setEntitySelector('');
      }
    }

    setDrawerVisible(true);
    if (field.pageNumber) {
      setCurrentPage(field.pageNumber);
    }
    
    // Show feedback message
    message.info({
      content: `Selected: ${field.pdfFieldName}`,
      duration: 1.5,
      key: 'field-select',
    });
  };

  // Handle mapping save
  const handleSaveMapping = async () => {
    if (!selectedField) return;
    
    if (!probateDocId) {
      message.error('Missing document ID');
      return;
    }

    const fieldId = String(selectedField.id);

    if (!fieldId) {
      console.error('Missing field ID for field:', selectedField);
      message.error(`Missing field ID. Field name: ${selectedField.pdfFieldName}`);
      return;
    }
    
    let payload;
    if (mappingMode === 'concat') {
      const template = advancedMode
        ? rawTemplate.trim()
        : buildTemplate(concatParts, separator);
      if (!template) {
        message.warning('Add at least one field to concatenate (or clear the mapping instead)');
        return;
      }
      // Keep relatedEntity/databaseFieldName pointing at the first part so
      // mapped-status and listings stay meaningful; template wins at fill time.
      const firstPart = concatParts.find(p => p.entity && p.field);
      payload = {
        template,
        relatedEntity: firstPart ? firstPart.entity : selectedField.relatedEntity || null,
        databaseFieldName: firstPart
          ? `${firstPart.entity}.${firstPart.field}`
          : selectedField.databaseFieldName || null,
        // Each placeholder carries its own row, so the column would only
        // contradict the template. Cleared rather than left stale.
        entitySelector: null,
      };
    } else {
      const isMultiple = !!MAPPABLE_ENTITIES[selectedEntity]?.multiple;
      payload = {
        relatedEntity: selectedEntity || null,
        databaseFieldName: selectedEntity && selectedFieldKey
          ? `${selectedEntity}.${selectedFieldKey}`
          : null,
        // Always sent: remapping a field to a singular entity has to drop any
        // row chosen for the entity it pointed at before.
        entitySelector: isMultiple ? entitySelector || null : null,
        template: null,
      };
    }

    setSaving(true);
    try {
      await probateDocsService.updateFieldMapping(probateDocId, fieldId, payload);
      message.success('Mapping saved successfully');
      fetchMappings();
      setDrawerVisible(false);
      setSelectedField(null);
    } catch (err: any) {
      console.error('Failed to save mapping:', err);
      message.error(err.response?.data?.message || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  // Handle entity change
  const handleEntityChange = (entity: string) => {
    setSelectedEntity(entity);
    setSelectedFieldKey('');
    setEntitySelector('');
  };

  // Filter fields based on search query
  const filteredFields = fields.filter(field => {
    const query = searchQuery.toLowerCase();
    return (
      field.pdfFieldName.toLowerCase().includes(query) ||
      (field.surroundingText && field.surroundingText.toLowerCase().includes(query)) ||
      (field.databaseFieldName && field.databaseFieldName.toLowerCase().includes(query))
    );
  });

  // Group fields by page
  const fieldsByPage = filteredFields.reduce((acc, field) => {
    const page = field.pageNumber > 0 ? field.pageNumber : 0;
    if (!acc[page]) acc[page] = [];
    acc[page].push(field);
    return acc;
  }, {} as Record<number, PdfFieldMapping[]>);

  const mappedCount = fields.filter(f => f.databaseFieldName || f.template).length;

  // Fields whose position couldn't be extracted (no page or zero-size rect)
  // can't be highlighted on the PDF — they're only mappable from the sidebar.
  const hasPosition = (f: PdfFieldMapping) =>
    (f.pageNumber || 0) > 0 && f.rect && f.rect.width > 0 && f.rect.height > 0;
  const unplacedCount = fields.filter(f => !hasPosition(f)).length;

  // Navigation
  const handleBack = () => navigate(`/probate-docs/${probateDocId}/map-fields`);
  const handleBackToDocs = () => navigate('/probate-docs');

  // Render field overlays for the current page
  const renderFieldOverlays = () => {
    const pageFields = fields.filter(f => f.pageNumber === currentPage && hasPosition(f));

    if (!pageDimensions) return null;

    return pageFields.map(field => {
      const isMapped = !!field.databaseFieldName || !!field.template;
      const isSelected = selectedField?.uid === field.uid;

      // Calculate top position based on coordinate system preference
      const topPosition = invertY 
        ? (pageDimensions.height - field.rect.y - field.rect.height) * scale
        : field.rect.y * scale;

      return (
        <Tooltip key={field.uid || field.id} title={field.pdfFieldName}>
          <FieldOverlay
            style={{
              left: field.rect.x * scale,
              top: topPosition,
              width: field.rect.width * scale,
              height: field.rect.height * scale,
            }}
            $isMapped={isMapped}
            $isSelected={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              handleFieldSelect(field);
            }}
          />
        </Tooltip>
      );
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Top Bar */}
      <TopBar>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack} type="text" />
          <StyledBreadcrumb
            items={[
              {
                title: 'Probate Documents',
                onClick: handleBackToDocs,
              },
              {
                title: 'Field Mapping',
                onClick: handleBack,
              },
              {
                title: 'Visual Mapper',
              },
            ]}
          />
        </Space>
        <Space>
          <Tag color="blue">{fields.length} Fields</Tag>
          <Tag color={mappedCount === fields.length ? 'green' : 'orange'}>
            {mappedCount}/{fields.length} Mapped
          </Tag>
        </Space>
      </TopBar>

      {/* Error Alert */}
      {error && (
        <Alert 
          message={error} 
          type="error" 
          showIcon 
          closable
          onClose={() => setError(null)}
          style={{ margin: 16 }}
        />
      )}

      <MainContent>
        {/* PDF Viewer */}
        <PdfViewerContainer>
          <PdfControlBar>
            <Button 
              icon={<ZoomOutOutlined />} 
              onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
              type="text"
              style={{ color: '#fff' }}
            />
            <span>{Math.round(scale * 100)}%</span>
            <Button 
              icon={<ZoomInOutlined />} 
              onClick={() => setScale(s => Math.min(2, s + 0.1))}
              type="text"
              style={{ color: '#fff' }}
            />
            <span style={{ margin: '0 16px', borderLeft: '1px solid #4B5563', height: 20 }} />
            <Button 
              icon={<LeftOutlined />} 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              type="text"
              style={{ color: '#fff' }}
            />
            <span>Page {currentPage} of {numPages || '--'}</span>
            <Button 
              icon={<RightOutlined />} 
              onClick={() => setCurrentPage(p => Math.min(numPages || 1, p + 1))}
              disabled={!numPages || currentPage >= numPages}
              type="text"
              style={{ color: '#fff' }}
            />
            <span style={{ margin: '0 16px', borderLeft: '1px solid #4B5563', height: 20 }} />
            <Button 
              icon={<ReloadOutlined />}
              onClick={loadPdf}
              type="text"
              style={{ color: '#fff' }}
              title="Reload PDF"
            />
            {Object.keys(testFills).length > 0 && (
              <Button
                danger
                size="small"
                onClick={() => setTestFills({})}
              >
                Clear {Object.keys(testFills).length} test fill{Object.keys(testFills).length > 1 ? 's' : ''}
              </Button>
            )}
            <Tooltip title={`Coordinate System: ${invertY ? 'Bottom-Left (Standard PDF)' : 'Top-Left (Web)'}`}>
              <Button 
                icon={<SettingOutlined />}
                onClick={() => setInvertY(!invertY)}
                type={invertY ? 'primary' : 'text'}
                ghost={invertY}
                style={{ color: invertY ? '#60A5FA' : '#fff' }}
              >
                {invertY ? 'Y-Inverted' : 'Y-Normal'}
              </Button>
            </Tooltip>
          </PdfControlBar>
          
          {pdfLoading && !pdfData ? (
            <PdfLoadingContainer>
              <Spin size="large" />
              <Text style={{ color: '#fff', marginTop: 16 }}>Loading PDF...</Text>
            </PdfLoadingContainer>
          ) : pdfError ? (
            <PdfLoadingContainer>
              <Alert 
                message="Failed to load PDF" 
                description={pdfError}
                type="error"
                showIcon
                action={
                  <Button onClick={loadPdf} size="small">
                    Retry
                  </Button>
                }
              />
            </PdfLoadingContainer>
          ) : fileDataUrl ? (
            <PdfWrapper $scale={1}>
              <Document
                key={fileDataUrl}
                file={fileDataUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <PdfLoadingContainer>
                    <Spin size="large" />
                    <Text style={{ color: '#fff', marginTop: 16 }}>Loading Document...</Text>
                  </PdfLoadingContainer>
                }
              >
                <div style={{ position: 'relative' }}>
                  <Page 
                    pageNumber={currentPage} 
                    scale={scale} 
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onLoadSuccess={onPageLoadSuccess}
                  />
                  {/* Overlay Container */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none' // Let clicks pass through to overlays but not container
                  }}>
                    <div style={{ pointerEvents: 'auto' }}>
                       {renderFieldOverlays()}
                    </div>
                  </div>
                </div>
              </Document>
            </PdfWrapper>
          ) : null}
        </PdfViewerContainer>

        {/* Fields Sidebar */}
        <FieldsSidebar>
          <SidebarHeader>
            <Title level={5} style={{ margin: 0, marginBottom: 12 }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              PDF Fields
            </Title>
            <SearchInput 
              placeholder="Search fields..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              allowClear
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Click a field to map it to database values
            </Text>

            <LegendContainer>
              <LegendItem>
                <div className="dot" style={{ background: '#10B981', border: '1px solid #059669' }} />
                <span>Mapped</span>
              </LegendItem>
              <LegendItem>
                <div className="dot" style={{ background: '#F59E0B', border: '1px solid #D97706' }} />
                <span>Unmapped</span>
              </LegendItem>
              <LegendItem>
                <div className="dot" style={{ background: '#3B82F6', border: '1px solid #2563EB' }} />
                <span>Selected</span>
              </LegendItem>
            </LegendContainer>
          </SidebarHeader>
          
          {/* Selected Field Banner */}
          {selectedField && (
            <SelectedFieldBanner>
              <div className="selected-label">Currently Selected</div>
              <div className="selected-name">{selectedField.pdfFieldName}</div>
              {selectedField.surroundingText && (
                <div className="selected-context">"{selectedField.surroundingText}"</div>
              )}
            </SelectedFieldBanner>
          )}
          
          <SidebarContent>
            {unplacedCount > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`${unplacedCount} field${unplacedCount > 1 ? 's have' : ' has'} no position data`}
                description="They can't be highlighted on the PDF but can still be mapped from this list. Re-extract fields to fix their positions."
              />
            )}
            {filteredFields.length === 0 ? (
              <Empty 
                description={searchQuery ? 'No matching fields' : 'No fields extracted'}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              Object.entries(fieldsByPage)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([page, pageFields]) => (
                  <div key={page}>
                    <Text strong style={{ 
                      display: 'block', 
                      marginBottom: 8, 
                      marginTop: 12,
                      color: '#6B7280' 
                    }}>
                      {Number(page) > 0 ? `Page ${page}` : 'No Position'}
                    </Text>
                    {pageFields.map(field => {
                      const isMapped = !!field.databaseFieldName || !!field.template;
                      const isSelected = selectedField?.uid === field.uid;
                      
                      return (
                        <FieldCard 
                          key={field.uid || field.id}
                          $isMapped={isMapped}
                          $isSelected={isSelected}
                          onClick={() => handleFieldSelect(field)}
                          size="small"
                        >
                          <FieldName>
                            {isMapped ? (
                              <CheckCircleOutlined style={{ color: '#10B981', marginRight: 6 }} />
                            ) : (
                              <WarningOutlined style={{ color: '#F59E0B', marginRight: 6 }} />
                            )}
                            {field.pdfFieldName}
                          </FieldName>
                          {field.surroundingText && (
                            <Tooltip title={field.surroundingText}>
                              <FieldContext>"{field.surroundingText}"</FieldContext>
                            </Tooltip>
                          )}
                          <FieldMeta>
                            <Tag color={field.type === 'TextField' ? 'blue' : 'default'} style={{ fontSize: 10 }}>
                              {field.type}
                            </Tag>
                            <PageIndicator>{field.pageNumber > 0 ? `Pg ${field.pageNumber}` : 'Pg ?'}</PageIndicator>
                          </FieldMeta>
                          {isMapped && (
                            <MappingInfo>
                              {field.template ? (
                                <>Template: <strong style={{ wordBreak: 'break-all' }}>{field.template}</strong></>
                              ) : (
                                <>Mapped to: <strong>{field.databaseFieldName}</strong></>
                              )}
                            </MappingInfo>
                          )}
                        </FieldCard>
                      );
                    })}
                  </div>
                ))
            )}
          </SidebarContent>
        </FieldsSidebar>
      </MainContent>

      {/* Mapping Drawer */}
      <Drawer
        title={
          <Space>
            <FileTextOutlined />
            <span>Map Field: {selectedField?.pdfFieldName}</span>
          </Space>
        }
        placement="right"
        width={450}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedField(null);
        }}
        open={drawerVisible}
        extra={
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveMapping}
            loading={saving}
          >
            Save Mapping
          </Button>
        }
      >
        {selectedField && (
          <DrawerContent>
            {/* Field Info */}
            <Card size="small" title="Field Information">
              <p><strong>Field Name:</strong> {selectedField.pdfFieldName}</p>
              <p><strong>Type:</strong> <Tag>{selectedField.type}</Tag></p>
              <p><strong>Page:</strong> {selectedField.pageNumber}</p>
              {selectedField.defaultValue && (
                <p><strong>Default Value:</strong> {selectedField.defaultValue}</p>
              )}
              {!selectedField.uid && (
                <Alert 
                  message="Missing Field ID" 
                  description="This field has no unique ID (UID) from the server. You may need to re-extract fields to fix this." 
                  type="warning" 
                  showIcon 
                  style={{ marginTop: 8 }}
                />
              )}
            </Card>

            {/* Visual Test Fill */}
            <Card size="small" title="Visual Test Fill">
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Fill this field in the PDF preview with test text to confirm it's the
                  right one and see exactly where data will land. Preview only — the
                  document is not modified.
                </Text>
                {selectedField.type === 'TextField' ? (
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      value={testValue}
                      onChange={e => setTestValue(e.target.value)}
                      placeholder="Test text..."
                    />
                    <Button
                      type="primary"
                      onClick={() => {
                        setTestFills(prev => ({
                          ...prev,
                          [selectedField.pdfFieldName]: testValue || 'TEST',
                        }));
                        if (selectedField.pageNumber > 0) setCurrentPage(selectedField.pageNumber);
                      }}
                    >
                      Fill
                    </Button>
                  </Space.Compact>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => {
                      setTestFills(prev => ({
                        ...prev,
                        [selectedField.pdfFieldName]: 'x',
                      }));
                      if (selectedField.pageNumber > 0) setCurrentPage(selectedField.pageNumber);
                    }}
                  >
                    Fill with test value
                  </Button>
                )}
                {!(selectedField.pageNumber > 0 && selectedField.rect?.width > 0) && (
                  <Alert
                    type="info"
                    showIcon
                    message="This field has no position data — after filling, flip through the pages to spot the test text."
                  />
                )}
                {testFills[selectedField.pdfFieldName] !== undefined && (
                  <Button
                    size="small"
                    onClick={() => {
                      setTestFills(prev => {
                        const next = { ...prev };
                        delete next[selectedField.pdfFieldName];
                        return next;
                      });
                    }}
                  >
                    Remove test fill from this field
                  </Button>
                )}
              </Space>
            </Card>

            {/* Context */}
            {selectedField.surroundingText && (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Context (Nearby Text):
                </Text>
                <ContextBox>"{selectedField.surroundingText}"</ContextBox>
              </div>
            )}

            {/* Position Info */}
            {selectedField.rect && (
              <PositionInfo>
                <h5>📍 Field Location</h5>
                <p>
                  Position: X={selectedField.rect.x}, Y={selectedField.rect.y}<br />
                  Size: {selectedField.rect.width}×{selectedField.rect.height}px
                </p>
              </PositionInfo>
            )}

            {/* Mapping Mode */}
            <FormGroup>
              <label>Mapping Type:</label>
              <Radio.Group
                value={mappingMode}
                onChange={e => setMappingMode(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="single">Single Field</Radio.Button>
                <Radio.Button value="concat">Concatenate Fields</Radio.Button>
              </Radio.Group>
            </FormGroup>

            {mappingMode === 'single' && (
              <>
                <FormGroup>
                  <label>Map to Entity:</label>
                  <Select
                    value={selectedEntity || undefined}
                    onChange={handleEntityChange}
                    placeholder="Select entity..."
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {mappableEntityOptions(selectedField.relatedEntity || undefined).map(([key, entity]) => (
                      <Option key={key} value={key}>{entity.label}</Option>
                    ))}
                  </Select>
                </FormGroup>

                {/* Which row. An estate has many assets, so "asset market
                    value" does not yet say what fills the box. */}
                {MAPPABLE_ENTITIES[selectedEntity]?.multiple && (
                  <FormGroup>
                    <label>Which one?</label>
                    <EntityRowSelector
                      entity={MAPPABLE_ENTITIES[selectedEntity]}
                      value={entitySelector}
                      onChange={setEntitySelector}
                      resetKey={`${selectedField.id}:${selectedEntity}`}
                    />
                  </FormGroup>
                )}

                {selectedEntity && MAPPABLE_ENTITIES[selectedEntity] && (
                  <FormGroup>
                    <label>Select Field:</label>
                    <Select
                      value={selectedFieldKey || undefined}
                      onChange={setSelectedFieldKey}
                      placeholder="Select field..."
                      allowClear
                      style={{ width: '100%' }}
                    >
                      {MAPPABLE_ENTITIES[selectedEntity].fields.map((f) => (
                        <Option key={f.value} value={f.value}>{f.label}</Option>
                      ))}
                    </Select>
                  </FormGroup>
                )}

                {/* Current Mapping Preview */}
                {selectedEntity && selectedFieldKey && (
                  <Alert
                    message="Mapping Preview"
                    description={
                      <span>
                        This field will be mapped to: <br />
                        <strong>{MAPPABLE_ENTITIES[selectedEntity]?.label}</strong> → {' '}
                        <strong>
                          {MAPPABLE_ENTITIES[selectedEntity]?.fields.find(f => f.value === selectedFieldKey)?.label}
                        </strong>
                        {MAPPABLE_ENTITIES[selectedEntity]?.multiple && (
                          <>
                            <br />
                            Row: <code>{entitySelector || '[0]'}</code>
                          </>
                        )}
                      </span>
                    }
                    type="info"
                    showIcon
                  />
                )}
              </>
            )}

            {mappingMode === 'concat' && (
              <>
                <FormGroup>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Fields to Concatenate:</label>
                    <Space size={4}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Advanced</Text>
                      <Switch
                        size="small"
                        checked={advancedMode}
                        onChange={(checked) => {
                          if (checked) {
                            // Seed the raw editor with whatever the builder has
                            const built = buildTemplate(concatParts, separator);
                            setRawTemplate(built || rawTemplate);
                          } else if (rawTemplate) {
                            const parsed = parseTemplate(rawTemplate);
                            if (parsed) {
                              setConcatParts(parsed.parts);
                              setSeparator(parsed.separator);
                            } else {
                              message.warning('Template is too complex for the builder — keeping advanced mode');
                              return;
                            }
                          }
                          setAdvancedMode(checked);
                        }}
                      />
                    </Space>
                  </div>
                </FormGroup>

                {advancedMode ? (
                  <FormGroup>
                    <Input.TextArea
                      value={rawTemplate}
                      onChange={e => setRawTemplate(e.target.value)}
                      rows={4}
                      placeholder={'e.g. {{user.firstName}} {{user.lastName}}, {{estate.estateName}}'}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Use {'{{entity.field}}'} placeholders; any text between them is kept
                      literally. For an entity an estate holds several of, say which one:
                      {' '}{'{{asset[1].marketValue}}'} or
                      {' '}{'{{participants[relationshipToDecedent=Surviving Spouse].fullName}}'}.
                      Without one, the first row fills the field.
                    </Text>
                  </FormGroup>
                ) : (
                  <>
                    {concatParts.map((part, index) => (
                      <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <Select
                            value={part.entity || undefined}
                            onChange={(entity) => {
                              setConcatParts(parts => parts.map((p, i) =>
                                i === index ? { entity, field: '', selector: '' } : p
                              ));
                            }}
                            placeholder="Entity..."
                            style={{ flex: 1 }}
                          >
                            {mappableEntityOptions(part.entity).map(([key, entity]) => (
                              <Option key={key} value={key}>{entity.label}</Option>
                            ))}
                          </Select>
                          <Select
                            value={part.field || undefined}
                            onChange={(fieldKey) => {
                              setConcatParts(parts => parts.map((p, i) =>
                                i === index ? { ...p, field: fieldKey } : p
                              ));
                            }}
                            placeholder="Field..."
                            disabled={!part.entity}
                            style={{ flex: 1.4 }}
                          >
                            {(MAPPABLE_ENTITIES[part.entity]?.fields || []).map((f) => (
                              <Option key={f.value} value={f.value}>{f.label}</Option>
                            ))}
                          </Select>
                          <Button
                            icon={<DeleteOutlined />}
                            danger
                            type="text"
                            disabled={concatParts.length <= 1}
                            onClick={() => setConcatParts(parts => parts.filter((_, i) => i !== index))}
                          />
                        </div>

                        {/* Each part picks its own row, so a template can name
                            two different participants or two assets. */}
                        {MAPPABLE_ENTITIES[part.entity]?.multiple && (
                          <div style={{ paddingLeft: 8 }}>
                            <EntityRowSelector
                              compact
                              entity={MAPPABLE_ENTITIES[part.entity]}
                              value={part.selector || ''}
                              onChange={(selector) => {
                                setConcatParts(parts => parts.map((p, i) =>
                                  i === index ? { ...p, selector } : p
                                ));
                              }}
                              resetKey={`${selectedField.id}:${index}:${part.entity}`}
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    <Button
                      icon={<PlusOutlined />}
                      type="dashed"
                      block
                      onClick={() => setConcatParts(parts => [...parts, { entity: '', field: '', selector: '' }])}
                    >
                      Add Field
                    </Button>

                    <FormGroup>
                      <label>Separator (between fields):</label>
                      <Select
                        value={separator}
                        onChange={setSeparator}
                        style={{ width: '100%' }}
                        options={[
                          { value: ' ', label: 'Space' },
                          { value: ', ', label: 'Comma + space' },
                          { value: ' - ', label: 'Dash' },
                          { value: '\n', label: 'New line' },
                          { value: '', label: 'None' },
                        ]}
                      />
                    </FormGroup>
                  </>
                )}

                {/* Template Preview */}
                {(() => {
                  const preview = advancedMode ? rawTemplate.trim() : buildTemplate(concatParts, separator);
                  return preview ? (
                    <Alert
                      message="Template Preview"
                      description={<code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{preview}</code>}
                      type="info"
                      showIcon
                    />
                  ) : (
                    <Alert
                      message="Pick at least one entity + field to build the template"
                      type="warning"
                      showIcon
                    />
                  );
                })()}
              </>
            )}

            {/* Clear Mapping Button */}
            {(selectedField.databaseFieldName || selectedField.template) && (
              <Button
                danger
                icon={<CloseOutlined />}
                loading={saving}
                onClick={async () => {
                  setSelectedEntity('');
                  setSelectedFieldKey('');
                  setEntitySelector('');
                  setConcatParts([{ entity: '', field: '' }]);
                  setRawTemplate('');
                  setSaving(true);
                  try {
                    await probateDocsService.updateFieldMapping(probateDocId, String(selectedField.id), {
                      relatedEntity: null,
                      databaseFieldName: null,
                      entitySelector: null,
                      template: null,
                    });
                    message.success('Mapping cleared');
                    fetchMappings();
                    setDrawerVisible(false);
                    setSelectedField(null);
                  } catch (err: any) {
                    message.error(err.response?.data?.message || 'Failed to clear mapping');
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Clear Mapping
              </Button>
            )}
          </DrawerContent>
        )}
      </Drawer>
    </PageContainer>
  );
};

export default PdfVisualMapper;